import { useCallback, useState } from 'react';
import {
  useImagePicker,
  type PickedImage,
} from 'shared/src/hooks';
import { isNotNil } from 'shared/src/utils';
import { useAttachPhotoBottomSheet } from './useAttachPhotoBottomSheet';
import { useUpdateFolderInfo } from '../queries/useUpdateFolderInfo';
import { useUploadCoverImage } from '../queries/useUploadCoverImage';

interface UseCoverEditorParams {
  folderSno: number;
  title: string | null | undefined;
  remoteCoverImage: string | null | undefined;
}

// 웹 picker 의 blob 미리보기 해제(네이티브 uri 는 no-op). 저장 직후엔 해제하지 않는다 —
// 서버 이미지가 내려오기 전이라 미리보기가 잠깐 빈다.
const revokePreview = (uri: string | undefined) => {
  if (uri?.startsWith('blob:') === true) URL.revokeObjectURL(uri);
};

export const useCoverEditor = ({
  folderSno,
  title,
  remoteCoverImage,
}: UseCoverEditorParams) => {
  const { show: showAttachPhoto } = useAttachPhotoBottomSheet();
  const { getImages, takePhoto } = useImagePicker();
  const { mutateAsync: uploadCoverImage } = useUploadCoverImage();
  const { mutateAsync: updateFolderInfo } = useUpdateFolderInfo();

  const [pendingPhoto, setPendingPhoto] = useState<PickedImage | null>(null);
  const [isReset, setIsReset] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);

  const previewUri = pendingPhoto?.uri ?? (isReset ? null : remoteCoverImage);
  const hasUnsavedChanges =
    !isSaved &&
    (pendingPhoto != null || (isReset && isNotNil(remoteCoverImage)));

  const promptAttach = useCallback(async () => {
    const result = await showAttachPhoto({
      hasCurrentPhoto: isNotNil(previewUri),
    });
    if (result.type !== 'confirm') return;
    switch (result.data.action) {
      case 'camera': {
        const [photo] = await takePhoto();
        if (photo == null) return;
        setPendingPhoto(prev => {
          revokePreview(prev?.uri);
          return photo;
        });
        setIsReset(false);
        setIsSaved(false);
        break;
      }
      case 'album': {
        const [photo] = await getImages({ currentSize: 0, maxPhotoSize: 1 });
        if (photo == null) return;
        setPendingPhoto(prev => {
          revokePreview(prev?.uri);
          return photo;
        });
        setIsReset(false);
        setIsSaved(false);
        break;
      }
      case 'reset': {
        setPendingPhoto(null);
        setIsReset(true);
        setIsSaved(false);
        break;
      }
    }
  }, [getImages, previewUri, showAttachPhoto, takePhoto]);

  const commit = useCallback(async () => {
    setIsCommitting(true);
    try {
      if (pendingPhoto != null) {
        const { data } = await uploadCoverImage({
          uri: pendingPhoto.uri,
          name: pendingPhoto.name,
          type: pendingPhoto.type,
          file: pendingPhoto.file,
        });
        if (data.image == null) throw new Error('upload returned empty image');
        await updateFolderInfo({
          sno: folderSno,
          title: title ?? '',
          image: data.image,
        });
        setPendingPhoto(null);
      } else if (isReset && isNotNil(remoteCoverImage)) {
        await updateFolderInfo({
          sno: folderSno,
          title: title ?? '',
          image: null,
        });
      }
      setIsSaved(true);
    } finally {
      setIsCommitting(false);
    }
  }, [
    folderSno,
    isReset,
    pendingPhoto,
    remoteCoverImage,
    title,
    updateFolderInfo,
    uploadCoverImage,
  ]);

  const reset = useCallback(() => {
    setPendingPhoto(null);
    setIsReset(false);
    setIsSaved(false);
  }, []);

  return {
    previewUri,
    hasUnsavedChanges,
    isCommitting,
    promptAttach,
    commit,
    reset,
  };
};
